import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react'
import type {
  ProcedureAttachment,
  Attachment,
} from '@dataTypes/claimFeature.types'
import type { MockFileItem } from '@pages/Files/mockFilesData'
import { v4 as uuid } from 'uuid'

function mockFileToProcedureAttachment(
  file: MockFileItem,
  index: number,
  existingCount: number,
): ProcedureAttachment {
  const itemNo = `CPA-${existingCount + index + 1}`
  const formatType = file.fileName.split('.').pop()?.toUpperCase() ?? 'UNKNOWN'
  const dateTaken = file.uploadDate
    ? new Date(file.uploadDate).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0]

  return {
    itemNo,
    item: file.fileName,
    formatType,
    orientation: 'Portrait',
    dateTaken,
    fileName: file.fileName.replace(/\s+/g, '_'),
    isEditing: false,
    notes: [],
    fileUrl: file.previewUrl ?? undefined,
    mapId: uuid(),
  }
}

function mockFileToClaimAttachment(
  file: MockFileItem,
  index: number,
  existingCount: number,
): Attachment {
  const itemNo = `CA-${existingCount + index + 1}`
  const formatType = file.fileName.split('.').pop()?.toUpperCase() ?? 'UNKNOWN'
  const dateTaken = file.uploadDate
    ? new Date(file.uploadDate).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0]

  return {
    itemNo,
    item: file.fileName,
    formatType,
    orientation: 'Portrait',
    dateTaken,
    fileName: file.fileName.replace(/\s+/g, '_'),
    isEditing: false,
    notes: [],
    fileUrl: file.previewUrl ?? undefined,
    mapId: uuid(),
  }
}

type OnProcedureCompleteCallback = (attachments: ProcedureAttachment[]) => void
type OnClaimAttachmentCompleteCallback = (attachments: Attachment[]) => void

type SelectionMode = 'procedure' | 'claimAttachment' | null

interface FileSelectionForClaimProcedureContextType {
  isOpen: boolean
  existingAttachmentCount: number
  openForClaimProcedure: (
    onComplete: OnProcedureCompleteCallback,
    existingCount: number,
  ) => void
  openForClaimAttachment: (
    onComplete: OnClaimAttachmentCompleteCallback,
    existingCount: number,
  ) => void
  close: () => void
  confirmSelection: (selectedFiles: MockFileItem[]) => void
}

const FileSelectionForClaimProcedureContext = createContext<
  FileSelectionForClaimProcedureContextType | undefined
>(undefined)

export const useFileSelectionForClaimProcedure =
  (): FileSelectionForClaimProcedureContextType => {
    const context = useContext(FileSelectionForClaimProcedureContext)
    if (!context) {
      throw new Error(
        'useFileSelectionForClaimProcedure must be used within FileSelectionForClaimProcedureProvider',
      )
    }
    return context
  }

interface FileSelectionForClaimProcedureProviderProps {
  children: React.ReactNode
}

export const FileSelectionForClaimProcedureProvider: React.FC<
  FileSelectionForClaimProcedureProviderProps
> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [existingAttachmentCount, setExistingAttachmentCount] = useState(0)
  const modeRef = useRef<SelectionMode>(null)
  const onProcedureCompleteRef = useRef<OnProcedureCompleteCallback | null>(
    null,
  )
  const onClaimAttachmentCompleteRef =
    useRef<OnClaimAttachmentCompleteCallback | null>(null)
  const existingCountRef = useRef(0)

  const openForClaimProcedure = useCallback(
    (onComplete: OnProcedureCompleteCallback, existingCount: number) => {
      modeRef.current = 'procedure'
      onProcedureCompleteRef.current = onComplete
      onClaimAttachmentCompleteRef.current = null
      existingCountRef.current = existingCount
      setExistingAttachmentCount(existingCount)
      setIsOpen(true)
    },
    [],
  )

  const openForClaimAttachment = useCallback(
    (onComplete: OnClaimAttachmentCompleteCallback, existingCount: number) => {
      modeRef.current = 'claimAttachment'
      onClaimAttachmentCompleteRef.current = onComplete
      onProcedureCompleteRef.current = null
      existingCountRef.current = existingCount
      setExistingAttachmentCount(existingCount)
      setIsOpen(true)
    },
    [],
  )

  const close = useCallback(() => {
    modeRef.current = null
    onProcedureCompleteRef.current = null
    onClaimAttachmentCompleteRef.current = null
    setIsOpen(false)
  }, [])

  const confirmSelection = useCallback((selectedFiles: MockFileItem[]) => {
    const count = existingCountRef.current
    const mode = modeRef.current

    if (selectedFiles.length > 0 && mode === 'procedure') {
      const callback = onProcedureCompleteRef.current
      if (callback) {
        const attachments = selectedFiles.map((f, i) =>
          mockFileToProcedureAttachment(f, i, count),
        )
        callback(attachments)
      }
    } else if (selectedFiles.length > 0 && mode === 'claimAttachment') {
      const callback = onClaimAttachmentCompleteRef.current
      if (callback) {
        const attachments = selectedFiles.map((f, i) =>
          mockFileToClaimAttachment(f, i, count),
        )
        callback(attachments)
      }
    }

    modeRef.current = null
    onProcedureCompleteRef.current = null
    onClaimAttachmentCompleteRef.current = null
    setIsOpen(false)
  }, [])

  return (
    <FileSelectionForClaimProcedureContext.Provider
      value={{
        isOpen,
        existingAttachmentCount,
        openForClaimProcedure,
        openForClaimAttachment,
        close,
        confirmSelection,
      }}
    >
      {children}
    </FileSelectionForClaimProcedureContext.Provider>
  )
}
